import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Icon, Input, Label, Message, Modal } from 'semantic-ui-react';
import {
  formatUnits,
  getReportableStock,
  getSaleUnitQuantity,
  parseSaleUnitQuantity,
} from './packStock';

const SHARED_REMOTE_INVENTORY_MESSAGE =
  'Estas publicaciones comparten inventario en Mercado Libre y deben usar la misma cantidad por pack. Por ahora no se pueden cambiar de a una.';

const isFullOnlyListing = (listing) =>
  listing.marketplace === 'mercado_libre' && String(listing.stockMode || 'standard') === 'full_only';

const formatSales = (quantity) => `${quantity} ${quantity === 1 ? 'venta' : 'ventas'}`;
const formatPublications = (quantity) => `${quantity} ${quantity === 1 ? 'publicación' : 'publicaciones'}`;

const getPhysicalStock = (listing) => {
  const quantity = Number(listing.sellerStockQuantity ?? listing.availableQuantity ?? 0);
  return Number.isFinite(quantity) ? Math.max(0, quantity) : 0;
};

const getFailureMessage = (error) => {
  const payload = error?.response?.data || error?.data || {};
  const responseMessage = payload.message;
  const code = error?.code || payload.code || payload.errorCode || payload.error || responseMessage?.code;
  if (code === 'PACK_SHARED_REMOTE_INVENTORY_MISMATCH') {
    return SHARED_REMOTE_INVENTORY_MESSAGE;
  }

  if (Array.isArray(responseMessage)) {
    return responseMessage.join(' ');
  }

  return responseMessage?.message || responseMessage || error?.message || 'No pudimos guardar los cambios. Inténtalo nuevamente.';
};

const initialDrafts = (listings) =>
  Object.fromEntries(listings.map((listing) => [listing.id, {
    saleUnitQuantity: String(getSaleUnitQuantity(listing.saleUnitQuantity)),
    sku: String(listing.sku || '').trim(),
  }]));

export default function PackConfigModal({ open, listings, onClose, onSaved, updateListing }) {
  const eligibleListings = useMemo(
    () => (Array.isArray(listings) ? listings : []).filter((listing) => !isFullOnlyListing(listing)),
    [listings],
  );
  const fullOnlyCount = (Array.isArray(listings) ? listings : []).length - eligibleListings.length;
  const [drafts, setDrafts] = useState({});
  const [savedListingIds, setSavedListingIds] = useState({});
  const [failures, setFailures] = useState({});
  const [skippedListingIds, setSkippedListingIds] = useState({});
  const [saving, setSaving] = useState(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setDrafts(initialDrafts(eligibleListings));
      setSavedListingIds({});
      setFailures({});
      setSkippedListingIds({});
    }
    wasOpen.current = open;
  }, [eligibleListings, open]);

  const changedCount = eligibleListings.filter((listing) => {
    const draft = drafts[listing.id] || {};
    const nextQuantity = parseSaleUnitQuantity(draft.saleUnitQuantity);
    return (
      !savedListingIds[listing.id] &&
      nextQuantity !== null &&
      (nextQuantity !== getSaleUnitQuantity(listing.saleUnitQuantity) || draft.sku.trim() !== String(listing.sku || '').trim())
    );
  }).length;
  const hasInvalidDraft = eligibleListings.some(
    (listing) => {
      const draft = drafts[listing.id] || {};
      return parseSaleUnitQuantity(draft.saleUnitQuantity) === null;
    },
  );
  const failureCount = Object.keys(failures).length;
  const skippedCount = Object.keys(skippedListingIds).length;

  const setDraft = (listingId, patch) => {
    setDrafts((previous) => ({ ...previous, [listingId]: { ...(previous[listingId] || {}), ...patch } }));
    setFailures((previous) => {
      const next = { ...previous };
      delete next[listingId];
      return next;
    });
  };

  const adjustDraft = (listingId, value, adjustment) => {
    const currentQuantity = parseSaleUnitQuantity(value) ?? 1;
    setDraft(listingId, { saleUnitQuantity: String(Math.max(1, currentQuantity + adjustment)) });
  };

  const handleSave = async () => {
    if (saving) return;

    const invalidListing = eligibleListings.find(
      (listing) => {
        const draft = drafts[listing.id] || {};
        return parseSaleUnitQuantity(draft.saleUnitQuantity) === null;
      },
    );
    if (invalidListing) {
      setFailures((previous) => ({
        ...previous,
        [invalidListing.id]: {
          listingId: invalidListing.id,
          title: invalidListing.title || 'Publicación',
          message: 'Ingresa una cantidad entera de 1 o más.',
          validation: true,
        },
      }));
      return;
    }

    const pendingListings = eligibleListings.filter((listing) => {
      const draft = drafts[listing.id] || {};
      const nextQuantity = parseSaleUnitQuantity(draft.saleUnitQuantity);
      return !savedListingIds[listing.id] && (
        nextQuantity !== getSaleUnitQuantity(listing.saleUnitQuantity) || draft.sku.trim() !== String(listing.sku || '').trim()
      );
    });
    if (pendingListings.length === 0) return;

    setSaving(true);
    const newSavedListingIds = {};
    const newFailures = {};
    const newSkippedListingIds = {};

    for (const listing of pendingListings) {
      const draft = drafts[listing.id] || {};
      const saleUnitQuantity = parseSaleUnitQuantity(draft.saleUnitQuantity);
      const sku = draft.sku.trim();
      try {
        const payload = { saleUnitQuantity };
        if (sku !== String(listing.sku || '').trim()) payload.sku = sku;
        const response = await updateListing(listing.id, payload);
        newSavedListingIds[listing.id] = true;
        if (String(response?.data?.stockSync?.status || '').toUpperCase().startsWith('SKIPPED')) {
          newSkippedListingIds[listing.id] = true;
        }
      } catch (error) {
        newFailures[listing.id] = {
          listingId: listing.id,
          title: listing.title || 'Publicación',
          message: getFailureMessage(error),
        };
      }
    }

    setSavedListingIds((previous) => ({ ...previous, ...newSavedListingIds }));
    setSkippedListingIds((previous) => ({ ...previous, ...newSkippedListingIds }));
    setFailures((previous) => {
      const next = { ...previous };
      pendingListings.forEach((listing) => delete next[listing.id]);
      return { ...next, ...newFailures };
    });
    setSaving(false);

    const summary = {
      savedCount: Object.keys(newSavedListingIds).length,
      failedCount: Object.keys(newFailures).length,
      skippedCount: Object.keys(newSkippedListingIds).length,
    };
    if (summary.savedCount > 0) {
      await onSaved?.(summary);
    }
    if (summary.failedCount === 0) {
      onClose();
    }
  };

  if (!open || eligibleListings.length === 0) return null;

  return (
    <Modal open size="large" closeIcon={!saving} closeOnDimmerClick={!saving} onClose={() => !saving && onClose()}>
      <Modal.Header>
        <div className="pack-config-modal__header">
          <span>Configurar packs</span>
          <Label circular className="pack-config-modal__count">
            {formatPublications(eligibleListings.length)}
          </Label>
        </div>
      </Modal.Header>
      <Modal.Content scrolling>
        <div className="pack-config-modal__content">
          <div>
            <p className="pack-config-modal__intro">
              Indica cuántas unidades del producto se descuentan cada vez que vendes una publicación.
            </p>
            <p className="pack-config-modal__help">Puedes usar una cantidad diferente en cada publicación.</p>
          </div>
          <div className="pack-config-modal__cards">
            {eligibleListings.map((listing) => {
              const draft = drafts[listing.id] || {};
              const configuredQuantity = draft.saleUnitQuantity ?? '1';
              const configuredSku = draft.sku ?? '';
              const validNextQuantity = parseSaleUnitQuantity(configuredQuantity);
              const currentQuantity = getSaleUnitQuantity(listing.saleUnitQuantity);
              const effectiveQuantity = validNextQuantity ?? currentQuantity;
              const isSaved = Boolean(savedListingIds[listing.id]);
              const isModified = !isSaved && validNextQuantity !== null && (
                validNextQuantity !== currentQuantity || configuredSku.trim() !== String(listing.sku || '').trim()
              );
              const physicalStock = getPhysicalStock(listing);
              const reportableStock = getReportableStock(physicalStock, effectiveQuantity);
              const remainder = physicalStock % effectiveQuantity;
              const failure = failures[listing.id];

              return (
                <div className="pack-config-modal__card" key={listing.id}>
                  <div className="pack-config-modal__card-top">
                    <div className="pack-config-modal__listing">
                      {listing.pictureUrl ? (
                        <img className="pack-config-modal__image" src={listing.pictureUrl} alt="" />
                      ) : (
                        <div aria-hidden="true" className="pack-config-modal__image-placeholder">
                          <Icon name="cube" size="large" />
                        </div>
                      )}
                      <div className="pack-config-modal__listing-copy">
                        <div className="pack-config-modal__listing-title-row">
                          <div className="pack-config-modal__listing-title">{listing.title || 'Publicación'}</div>
                          {isModified ? <Label className="pack-config-modal__modified">Modificado</Label> : null}
                          {isSaved ? <Label color="green">Guardado</Label> : null}
                        </div>
                        <div className="pack-config-modal__current">
                          Actualmente descuenta {formatUnits(currentQuantity)} por venta.
                        </div>
                      </div>
                    </div>
                    <div className="pack-config-modal__quantity">
                      <label htmlFor={`pack-quantity-${listing.id}`}>Unidades por venta</label>
                      <div className="pack-config-modal__quantity-control">
                        <Button
                          type="button"
                          basic
                          icon="minus"
                          aria-label={`Restar una unidad a ${listing.title || listing.id}`}
                          disabled={saving || isSaved || validNextQuantity === 1}
                          onClick={() => adjustDraft(listing.id, configuredQuantity, -1)}
                        />
                        <Input
                          id={`pack-quantity-${listing.id}`}
                          type="number"
                          min="1"
                          step="1"
                          value={configuredQuantity}
                          onChange={(event) => setDraft(listing.id, { saleUnitQuantity: event.target.value })}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleSave();
                            }
                          }}
                          disabled={saving || isSaved}
                          aria-label={`Unidades por venta para ${listing.title || listing.id}`}
                        />
                        <Button
                          type="button"
                          basic
                          icon="plus"
                          aria-label={`Sumar una unidad a ${listing.title || listing.id}`}
                          disabled={saving || isSaved}
                          onClick={() => adjustDraft(listing.id, configuredQuantity, 1)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="pack-config-modal__sku">
                    <label htmlFor={`pack-sku-${listing.id}`}>SKU que impactará</label>
                    <Input
                      id={`pack-sku-${listing.id}`}
                      value={configuredSku}
                      placeholder="SKU de la publicación"
                      disabled={saving || isSaved}
                      onChange={(event) => setDraft(listing.id, { sku: event.target.value })}
                      aria-label={`SKU que impactará para ${listing.title || listing.id}`}
                    />
                  </div>
                  <div className={`pack-config-modal__summary${reportableStock === 0 ? ' pack-config-modal__summary--empty' : ''}`}>
                    <div className="pack-config-modal__pack-name">
                      <Icon name="cube" color="violet" size="large" />
                      <div>
                        <strong>{effectiveQuantity === 1 ? 'Venta individual' : `Pack x${effectiveQuantity}`}</strong>
                        <div>Cada venta descontará {formatUnits(effectiveQuantity)}.</div>
                      </div>
                    </div>
                    <div className="pack-config-modal__summary-divider" />
                    <div>
                      {validNextQuantity === null ? (
                        <div className="pack-config-modal__invalid">Ingresa un número entero de 1 o más.</div>
                      ) : reportableStock === 0 ? (
                        <div className="pack-config-modal__empty-stock">
                          Esta publicación quedará sin stock. Tienes {formatUnits(physicalStock)} y cada venta necesita {formatUnits(effectiveQuantity)}.
                        </div>
                      ) : (
                        <>
                          <div className="pack-config-modal__stock-preview">
                            Stock del producto: <strong>{formatUnits(physicalStock)}</strong>
                            <span>→</span>
                            Podrás ofrecer: <strong>{formatSales(reportableStock)}</strong>
                          </div>
                          {remainder > 0 ? (
                            <div className="pack-config-modal__remainder">
                              Quedarán {formatUnits(remainder)} que no alcanzan para otra venta.
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                  {failure ? <Message negative className="pack-config-modal__failure" content={failure.message} /> : null}
                </div>
              );
            })}
          </div>
          <Message info className="pack-config-modal__info">
            El stock disponible para vender se calcula automáticamente. Esta configuración no cambia cuántas unidades tienes en stock; solo define cuántas se descuentan con cada venta.
          </Message>
          {fullOnlyCount > 0 ? (
            <Message warning className="pack-config-modal__full-warning">
              {fullOnlyCount === 1
                ? 'Se omitirá 1 publicación de Mercado Libre Full porque su stock lo controla Mercado Libre.'
                : `Se omitirán ${fullOnlyCount} publicaciones de Mercado Libre Full porque su stock lo controla Mercado Libre.`}
            </Message>
          ) : null}
          {failureCount > 0 ? (
            <Message negative className="pack-config-modal__failure-summary">
              No se pudieron guardar {formatPublications(failureCount)}. Revisa el motivo en cada publicación e inténtalo nuevamente.
            </Message>
          ) : null}
          {skippedCount > 0 ? (
            <Message warning className="pack-config-modal__skipped-summary">
              Guardamos {formatPublications(skippedCount)}, pero el canal no permitió actualizar su stock ahora. Revisa el estado de la publicación.
            </Message>
          ) : null}
        </div>
      </Modal.Content>
      <Modal.Actions className="pack-config-modal__actions">
        <span>
          {formatPublications(eligibleListings.length)} · {changedCount}{' '}
          {changedCount === 1 ? 'modificada' : 'modificadas'}
        </span>
        <div>
          <Button onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button color="violet" loading={saving} disabled={saving || (changedCount === 0 && !hasInvalidDraft)} onClick={handleSave}>
            {saving ? 'Guardando cambios…' : 'Guardar cambios'}
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
}
